import { lazy } from 'react';
import { publicUseCasePages } from '@/pages/publicSite/publicUseCaseContent';
import { getPublicSitePageRobots, isPublicSitePageEnabled } from '@/publicSite/publicSiteConfig';
import type { AppRouteDefinition } from './routes';

const AppFeaturesPage = lazy(async () => {
  const module = await import('@/features/features/routes');
  return { default: module.FeaturesPage };
});

const PublicFeaturesPage = lazy(async () => {
  const module = await import('../../pages/FeaturesPage');
  return { default: module.FeaturesPage };
});

const PlansPage = lazy(async () => {
  const module = await import('../../pages/PlansPage');
  return { default: module.PlansPage };
});

const UseCasesPage = lazy(async () => {
  const module = await import('../../pages/UseCasesPage');
  return { default: module.UseCasesPage };
});

const PublicUseCasePage = lazy(async () => {
  const module = await import('@/pages/publicSite/PublicUseCasePage');
  return { default: module.PublicUseCasePage };
});

const publicUseCaseRouteDefinitions: AppRouteDefinition[] = publicUseCasePages.map((page) => ({
  key: `use-case-${page.slug}`,
  path: page.path,
  enabled: isPublicSitePageEnabled('useCases'),
  devOnly: true,
  title: page.title,
  description: page.description,
  metadata: {
    canonicalPath: page.path,
    imageAlt: page.imageAlt,
    imagePath: '/logo.png',
    robots: getPublicSitePageRobots(),
    twitterCard: 'summary',
  },
  element: () => <PublicUseCasePage content={page} />,
}));

export const publicSiteRouteDefinitions: AppRouteDefinition[] = [
  {
    key: 'public-features',
    path: '/features',
    title: 'Features',
    description:
      'FlowQA helps teams preserve CI test results, compare recurring failures, track flaky tests, and drill from release questions into runs, suites, and tests.',
    metadata: {
      canonicalPath: '/features',
      imageAlt: 'FlowQA test result visibility and QA reporting',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: ({ session }) => (session ? <AppFeaturesPage /> : <PublicFeaturesPage />),
  },
  {
    key: 'plans',
    path: '/plans',
    enabled: isPublicSitePageEnabled('plans'),
    devOnly: true,
    title: 'Plans',
    description:
      'Compare FlowQA Free, Solo, and Team plans for CI test result history, JUnit uploads, Playwright visibility, run capacity, retention, and QA reporting.',
    metadata: {
      canonicalPath: '/plans',
      imageAlt: 'FlowQA plans for QA reporting and test run history',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: () => <PlansPage />,
  },
  {
    key: 'use-cases',
    path: '/use-cases',
    enabled: isPublicSitePageEnabled('useCases'),
    devOnly: true,
    title: 'Use Cases',
    description:
      'Explore how FlowQA supports CI test history, flaky test tracking, and QA reporting use cases for teams that need durable test evidence.',
    metadata: {
      canonicalPath: '/use-cases',
      imageAlt: 'FlowQA use cases for CI test history and QA reporting',
      imagePath: '/logo.png',
      robots: getPublicSitePageRobots(),
      twitterCard: 'summary',
    },
    element: () => <UseCasesPage />,
  },
  ...publicUseCaseRouteDefinitions,
];
